import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "commons/store/store";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import {
  ElementResourceTransferElementResourceTransfersStatusIdRead,
  ElementResourceTransferElementResourceTransfersViewIdRead,
} from "types/api.types";
import { downloadBlobFile } from "utils/files/downloadBlobFile";

interface TransferFile {
  data: any;
  status: ElementResourceTransferElementResourceTransfersStatusIdRead;
  viewLink: ElementResourceTransferElementResourceTransfersViewIdRead;
  name: any;
  randomize: any;
  isDownloading: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  error: any;
}

const initialState: TransferFile = {
  data: [],
  status: {},
  viewLink: {},
  name: [],
  randomize: {},
  isDownloading: false,
  isLoading: false,
  isDeleting: false,
  error: null
};

export const fetchOne = createAsyncThunk(
  "ert/fetchOne", async (id, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.get<any>(`/api/element_resource_transfers/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

type CollectionParams = {
  elementId: string;
};

export const fetchCollection = createAsyncThunk(
  "ert/fetchCollection", async (params: CollectionParams, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.get<any>(`/api/element_resource_transfers?element=${params.elementId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteOne = createAsyncThunk(
  "ert/fetchOne", async (id: string, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.delete<any>(`/api/element_resource_transfers/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

type updateElementParams = {
  elementId: string;
  ecrfId: string;
};

export const transferFile = createAsyncThunk(
  "transfer/file", async ({ elementId, ecrfId }: updateElementParams, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.put<any>(
        `/api/element_datas/${elementId}/transfer`,
        {
          ecrf: `/api/ecrves/${ecrfId}`,
        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const transferDownloadFile = createAsyncThunk(
  "transfer/downloadFile",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/element_resource_transfers/${id}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `tf_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const transferDownloadFiles = createAsyncThunk(
  "transfer/downloadFiles",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/element_datas/${id}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `tf_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]);
        }
      }

      downloadBlobFile(new Blob([response.data]), fileName);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const transferName = createAsyncThunk(
  "transfer/name", async (viewUrl: string, thunkAPI) => {
  try {
    const response = await axiosSecureInstance.get<any>(viewUrl);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const transferStatus = createAsyncThunk(
  "transfer/status", async (statusUrl: string, thunkAPI) => {
    try {
      const response =
        await axiosSecureInstance.get<ElementResourceTransferElementResourceTransfersStatusIdRead>(statusUrl);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const transferViewLink = createAsyncThunk(
  "transfer/viewLink", async (url: string, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.get<ElementResourceTransferElementResourceTransfersViewIdRead>(url);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const elementResourceTransferSlice = createSlice({
  name: 'elementResourceTransfer',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(deleteOne.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteOne.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteOne.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
    builder.addCase(transferFile.fulfilled, (state, action) => {
      state.data = action.payload;
    });
    builder
      .addCase(transferDownloadFile.pending, (state) => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(transferDownloadFile.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(transferDownloadFile.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload;
      });
    builder
      .addCase(transferDownloadFiles.pending, (state) => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(transferDownloadFiles.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(transferDownloadFiles.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload;
      });
    builder.addCase(transferName.fulfilled, (state, action) => {
      state.name = action.payload;
    });
    builder.addCase(transferStatus.fulfilled, (state, action) => {
      state.status = action.payload;
    });
    builder.addCase(transferViewLink.fulfilled, (state, action) => {
      state.viewLink = action.payload;
    });
  },
});

export const ertLoading = (state: RootState) => state.ecrf.elementResourceTransfer.isLoading;
export const ertDeleting = (state: RootState) => state.ecrf.elementResourceTransfer.isDeleting;
export const selectTransferFile = (state: RootState) => state.ecrf.elementResourceTransfer.data;
export const selectTransferFileDownloading = (state: RootState) => state.ecrf.elementResourceTransfer.isDownloading;
export const selectTransferFileName = (state: RootState) => state.ecrf.elementResourceTransfer.name;
export const selectTransferFileStatus = (state: RootState) => state.ecrf.elementResourceTransfer.status;
export const selectTransferFileViewLink = (state: RootState) => state.ecrf.elementResourceTransfer.viewLink;

export default elementResourceTransferSlice.reducer;
